import { useEffect, useRef, useCallback } from 'react';
import { useTripStore } from '@/stores/trip';
import { offlineGpsPing } from '@/lib/offline-api';
import { isNative } from '@/lib/platform';
import { startNativeGps, stopNativeGps } from '@/lib/native-gps';

/** On native: send pings every 10s (GPS fires every 5s). On browser: every 30s. */
const GPS_PING_INTERVAL = isNative() ? 10_000 : 30_000;

/**
 * Global GPS tracker — runs on every page while a trip is active.
 * On native (Capacitor): uses foreground service + high-frequency GPS.
 * On browser: uses navigator.geolocation.watchPosition.
 */
export function GpsTracker() {
  const { activeTrip, isTracking, gpsWatchId, addGpsPoint, setGpsWatchId } = useTripStore();
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const nativeRunningRef = useRef(false);

  const requestWakeLock = useCallback(async () => {
    if (isNative()) return; // Not needed on native — foreground service handles it
    if ('wakeLock' in navigator && !wakeLockRef.current) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      } catch {
        // Wake Lock not available
      }
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    wakeLockRef.current?.release();
    wakeLockRef.current = null;
  }, []);

  // Start/stop GPS watching based on tracking state
  useEffect(() => {
    if (!isTracking || !activeTrip) {
      // Stop watching
      if (isNative()) {
        if (nativeRunningRef.current) {
          stopNativeGps();
          nativeRunningRef.current = false;
        }
      } else {
        if (gpsWatchId !== null) {
          navigator.geolocation.clearWatch(gpsWatchId);
          setGpsWatchId(null);
        }
        releaseWakeLock();
      }
      return;
    }

    // --- NATIVE PATH ---
    if (isNative()) {
      if (nativeRunningRef.current) return; // Already tracking
      nativeRunningRef.current = true;

      startNativeGps((point) => {
        addGpsPoint({
          lat: point.lat,
          lng: point.lng,
          timestamp: point.timestamp,
          accuracy: point.accuracy,
        });
      });
      return;
    }

    // --- BROWSER PATH ---
    if (gpsWatchId !== null) return; // Already watching

    if ('geolocation' in navigator) {
      requestWakeLock();

      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          addGpsPoint({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            timestamp: pos.timestamp,
            accuracy: pos.coords.accuracy,
          });
        },
        () => {
          // GPS error — silently continue, will retry
        },
        { enableHighAccuracy: true, timeout: 15_000, maximumAge: 10_000 },
      );
      setGpsWatchId(watchId);
    }
  }, [isTracking, activeTrip, gpsWatchId, addGpsPoint, setGpsWatchId, requestWakeLock, releaseWakeLock]);

  // Send GPS pings to API
  useEffect(() => {
    if (!isTracking || !activeTrip) {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      return;
    }

    if (pingIntervalRef.current) return;

    const sendPing = () => {
      const state = useTripStore.getState();
      const points = state.activeTrip?.gpsPoints ?? [];
      const tripId = state.activeTrip?.tripId;
      if (points.length === 0 || !tripId) return;

      const latest = points[points.length - 1];
      if (!latest) return;

      offlineGpsPing({
        tripId,
        lat: latest.lat,
        lng: latest.lng,
        accuracy: latest.accuracy,
        timestamp: new Date(latest.timestamp).toISOString(),
      }).catch(() => { /* retry next interval */ });
    };

    // Send first ping after 5 seconds, then at interval
    const initialTimeout = setTimeout(sendPing, 5_000);
    pingIntervalRef.current = setInterval(sendPing, GPS_PING_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [isTracking, activeTrip]);

  // Re-acquire wake lock when page becomes visible again (browser only)
  useEffect(() => {
    if (isNative()) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isTracking) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isTracking, requestWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isNative()) {
        stopNativeGps();
        nativeRunningRef.current = false;
      } else {
        releaseWakeLock();
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [releaseWakeLock]);

  return null;
}
